import {
    MutableRefObject,
    ReactNode,
    useCallback,
    useState,
    useRef,
    useEffect,
    TouchEvent
} from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";
import { FormattedMessage, useIntl, defineMessage } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";
import FeatureGates from "@atlassian/feature-gate-js-client";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { Adg4Button } from "@/components/adg4/button/src";
import {
    Adg4DraggableTab,
    Adg4DraggableTabList,
    Adg4Tabs
} from "@/components/adg4/tabs/src";
import { Adg4TextUnderline } from "@/components/adg4/text-underline/src";
import { Picture } from "@/components/wpl/image/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { DataEventProps, Row } from "@/libraries/wpl/foundations/src";
import { OptionalExceptFor } from "@/utils/typescript-utils.ts";

import HeroTooltip from "./hero-tooltip.tsx";
import { TEAM_DATA, Team } from "./tab-contents/team-data.tsx";

/* for variant 1 and 2 */

interface NameInterface {
    id: string;
    defaultMessage: string;
}

/* variant 1 data */

type JiraView = {
    videoLink: string;
    image: string;
    videoLength: number;
    tooltipText: NameInterface;
};

const jiraViews: JiraView[] = [
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_HD (0-00-00-00)-4060786602.webp",
        videoLength: 9400,
        tooltipText: defineMessage({
            id: "HomePageTemplateV2.HeroTooltip.Board",
            defaultMessage: "Go from to-do to done"
        })
    },
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_B_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_B_HD (0-00-09-24)-1969559334.webp",
        videoLength: 7180,
        tooltipText: defineMessage({
            id: "HomePageTemplateV2.HeroTooltip.Timeline",
            defaultMessage: "Plan it out in order"
        })
    },
    {
        videoLink:
            "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_C_LowBR.mp4",
        image: "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_C_HD (0-00-16-35)-1443600009.webp",
        videoLength: 4750,
        tooltipText: defineMessage({
            id: "HomePageTemplateV2.HeroTooltip.Calendar",
            defaultMessage: "View what's coming up next"
        })
    }
];

/* variant 2 data */

const DEFAULT_TEAM_DATA: ActiveTeamDataProps = {
    id: "default",
    videoLink:
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_FULL_LowBR.mp4",
    heroImage:
        "https://wac-cdn.atlassian.com/misc-assets/webp-images/CSD-10721_WAC_Hero_A_HD (0-00-00-00)-4060786602.webp"
};

type HeroJiraCTAProps = {
    onSelectHeroTab: (index: number | undefined) => void;
    heroSelectedTeamIndex?: number;
};

type ActiveTeamDataProps = OptionalExceptFor<Team, "heroImage">;

export const HeroJiraCTA = ({
    heroSelectedTeamIndex,
    onSelectHeroTab
}: HeroJiraCTAProps) => {
    const showTreatmentHero = FeatureGates.getExperimentValue(
        "gwp-1705-homepage-hero",
        "showTreatment",
        false
    );

    const intl = useIntl();

    const leftArrowTranslatedLabel = intl.formatMessage({
        id: "HeroJiraCTA.leftArrow.aria-label",
        defaultMessage: "Left arrow"
    });

    const leftArrowDataEventProps: DataEventProps = {
        "data-product-key": "home",
        "data-event-component": "button",
        "data-event-container": "heroSection",
        "data-event": "clicked",
        "data-label": leftArrowTranslatedLabel,
        "data-label-english": "Left arrow"
    };

    const rightArrowTranslatedLabel = intl.formatMessage({
        id: "HeroJiraCTA.rightArrow.aria-label",
        defaultMessage: "Right arrow"
    });

    const rightArrowDataEventProps: DataEventProps = {
        "data-product-key": "home",
        "data-event-component": "button",
        "data-event-container": "heroSection",
        "data-event": "clicked",
        "data-label": rightArrowTranslatedLabel,
        "data-label-english": "Right arrow"
    };

    /* variant 1 */
    const [currSlide, setCurrSlide] = useState(0);
    const carouselRefs = useRef<(HTMLVideoElement | null)[]>([]);

    const handleChangeSlide = useCallback(
        (action: "prev" | "next") => {
            const newSlide =
                action === "prev"
                    ? currSlide == 0
                        ? jiraViews.length - 1
                        : currSlide - 1
                    : currSlide == jiraViews.length - 1
                    ? 0
                    : currSlide + 1;
            const video = carouselRefs.current[newSlide] as HTMLVideoElement;
            video.load();
            setCurrSlide(newSlide);
        },
        [currSlide]
    );

    useEffect(() => {
        if (showTreatmentHero) return;
        const interval = setInterval(() => {
            handleChangeSlide("next");
        }, jiraViews[currSlide]?.videoLength);
        return () => {
            clearInterval(interval);
        };
    }, [handleChangeSlide, currSlide, showTreatmentHero]);

    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const minSwipeDistance = 50;

    const onTouchStart = (e: TouchEvent) => {
        setTouchEnd(null);
        if (e.targetTouches[0]) setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: TouchEvent) => {
        if (e.targetTouches[0]) setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;
        if (isLeftSwipe) handleChangeSlide("prev");
        if (isRightSwipe) handleChangeSlide("next");
    };

    /* variant 2 */

    const cardRef: MutableRefObject<HTMLDivElement | null> = useRef(null);
    const defaultVideoRef: MutableRefObject<HTMLVideoElement | null> =
        useRef(null);
    const { ref: scrollRef, inView: sectionIsVisible } = useInView();

    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                cardRef.current &&
                !cardRef.current.contains(event.target as Node)
            ) {
                if (defaultVideoRef.current) defaultVideoRef.current.load();
                onSelectHeroTab(undefined);
            }
        },
        [onSelectHeroTab]
    );

    useEffect(() => {
        // Attach the listener on component mount
        document.addEventListener("mousedown", handleClickOutside);

        // Detach the listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    const [activeTeamData, setActiveTeamData] =
        useState<ActiveTeamDataProps>(DEFAULT_TEAM_DATA);

    useEffect(() => {
        if (typeof heroSelectedTeamIndex !== "number") {
            setActiveTeamData(DEFAULT_TEAM_DATA);
            return;
        }
        setActiveTeamData(
            TEAM_DATA[heroSelectedTeamIndex] || DEFAULT_TEAM_DATA
        );
        return;
    }, [heroSelectedTeamIndex]);

    const renderActiveMedia = () => {
        const { videoLink, heroImage } = activeTeamData;
        return (
            <>
                {videoLink ? (
                    <video
                        playsInline={true}
                        muted={true}
                        preload="auto"
                        css={[videoImgContainerStyles]}
                        poster={heroImage}
                        autoPlay={true}
                        ref={defaultVideoRef}
                    >
                        {heroImage}
                        <source src={videoLink} type="video/mp4" />
                    </video>
                ) : (
                    <Picture
                        css={[videoImgContainerStyles]}
                        image={{
                            src: heroImage,
                            alt: ""
                        }}
                    />
                )}
            </>
        );
    };

    return (
        <UFOSegment name="homepage-v2-hero">
            <section id="hero-jira-cta" css={jiraHeroStyles}>
                <Row>
                    <div>
                        <h2
                            className="superhero-contrast"
                            css={heroTitleStyles}
                        >
                            <FormattedMessage
                                id="HomePageTemplateV2.jiraHero"
                                defaultMessage="The new Jira:{br}from <teams>teams</teams> to dreams"
                                values={{
                                    teams: (str: ReactNode) => (
                                        <Adg4TextUnderline
                                            underline={
                                                <svg
                                                    width="100%"
                                                    height="30"
                                                    viewBox="0 0 306 42"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M0.314542 22.3672C52.9464 14.9125 91.8245 33.6666 144.966 18.1254C147.46 24.7349 153.892 33.0058 163.497 33.1094C155.486 32.9725 142.641 15.7178 155.536 27.2686C163.119 36.0683 196.172 25.2364 170.842 32.944C188.334 31.2885 204.356 20.5339 216.163 10.9702C235.811 -2.65832 220.928 23.6737 235.178 36.8756C255.37 55.2242 316.463 7.29572 304.134 7.28816C296.296 13.2947 285.769 17.046 281.423 23.6985C204.757 63.1757 259.992 -9.72674 214.311 2.55426C139.744 53.2533 177.393 7.4163 133.572 10.6223C108.06 28.9946 -0.613806 0.46356 0.29434 22.3672L0.314542 22.3672Z"
                                                        fill="#FFA900"
                                                    />
                                                </svg>
                                            }
                                        >
                                            {str}
                                        </Adg4TextUnderline>
                                    ),
                                    br: <br />
                                }}
                            />
                        </h2>

                        <Adg4Button
                            variant="primary"
                            color="blue"
                            href={useLocalizedHref(
                                "/try/cloud/signup?bundle=jira-software"
                            )}
                            dataEventProps={{
                                "data-event": "clicked",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-product-key": "home",
                                "data-label-english": "Get started"
                            }}
                            css={heroGetStartedStyles}
                        >
                            {
                                <FormattedMessage
                                    id="HomePageTemplateV2.jiraHero.getStartedCtaButton"
                                    defaultMessage="Get started"
                                />
                            }
                        </Adg4Button>

                        {showTreatmentHero && (
                            <div ref={cardRef}>
                                <div ref={scrollRef}>
                                    <Adg4Tabs>
                                        <Adg4DraggableTabList
                                            css={[
                                                tabsStyle,
                                                sectionIsVisible &&
                                                    teamCardsVisible
                                            ]}
                                        >
                                            {TEAM_DATA.map(
                                                (
                                                    {
                                                        title,
                                                        id,
                                                        icon,
                                                        iconHover,
                                                        englishLabel
                                                    },
                                                    index
                                                ) => {
                                                    return (
                                                        <Adg4DraggableTab
                                                            key={id}
                                                            onClick={() => {
                                                                onSelectHeroTab(
                                                                    index
                                                                );
                                                            }}
                                                        >
                                                            <button
                                                                css={
                                                                    buttonResetStyle
                                                                }
                                                                data-product-key="home"
                                                                data-event-component="button"
                                                                data-event-container="heroSection"
                                                                data-event="clicked"
                                                                data-label-english={
                                                                    englishLabel
                                                                }
                                                                aria-label={intl.formatMessage(
                                                                    title
                                                                )}
                                                            >
                                                                <div
                                                                    css={[
                                                                        teamCardStyles,
                                                                        heroSelectedTeamIndex ===
                                                                            index &&
                                                                            teamCardActiveStyles
                                                                    ]}
                                                                >
                                                                    <div
                                                                        css={[
                                                                            iconContainerStyles
                                                                        ]}
                                                                        className="default-icon"
                                                                    >
                                                                        {
                                                                            <Picture
                                                                                image={{
                                                                                    src: `${icon}`,
                                                                                    alt: "",
                                                                                    draggable:
                                                                                        "false"
                                                                                }}
                                                                            />
                                                                        }
                                                                    </div>
                                                                    <div
                                                                        css={[
                                                                            iconContainerStyles
                                                                        ]}
                                                                        className="hover-icon"
                                                                    >
                                                                        <Picture
                                                                            image={{
                                                                                src: `${
                                                                                    heroSelectedTeamIndex !==
                                                                                    index
                                                                                        ? iconHover
                                                                                        : icon
                                                                                }`,
                                                                                alt: "",
                                                                                draggable:
                                                                                    "false"
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    <span
                                                                        css={
                                                                            teamCardTextStyles
                                                                        }
                                                                    >
                                                                        {intl.formatMessage(
                                                                            title
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </button>
                                                        </Adg4DraggableTab>
                                                    );
                                                }
                                            )}
                                        </Adg4DraggableTabList>
                                    </Adg4Tabs>
                                </div>
                            </div>
                        )}

                        <Row layout="10">
                            <div>
                                <div css={videoContainerStyles}>
                                    {showTreatmentHero ? (
                                        <>{renderActiveMedia()}</>
                                    ) : (
                                        <div css={carouselStyles}>
                                            <div
                                                onTouchStart={onTouchStart}
                                                onTouchMove={onTouchMove}
                                                onTouchEnd={onTouchEnd}
                                            >
                                                {jiraViews.map(
                                                    (
                                                        {
                                                            videoLink,
                                                            image,
                                                            tooltipText
                                                        },
                                                        index
                                                    ) => (
                                                        <HeroTooltip
                                                            key={index}
                                                            content={
                                                                <div
                                                                    css={
                                                                        tooltipStyle
                                                                    }
                                                                >
                                                                    <p
                                                                        style={{
                                                                            margin: "0",
                                                                            lineHeight:
                                                                                "0.75rem",
                                                                            fontFamily:
                                                                                "var(--bxp-charlie-display-font-family)",
                                                                            fontWeight:
                                                                                "500",
                                                                            fontSize:
                                                                                "1.125rem",
                                                                            whiteSpace:
                                                                                "nowrap"
                                                                        }}
                                                                    >
                                                                        {intl.formatMessage(
                                                                            tooltipText
                                                                        )}
                                                                    </p>
                                                                </div>
                                                            }
                                                        >
                                                            <video
                                                                playsInline={
                                                                    true
                                                                }
                                                                muted={true}
                                                                preload="auto"
                                                                css={[
                                                                    videoImgContainerStyles,
                                                                    currSlide ==
                                                                    index
                                                                        ? videoActiveStyles
                                                                        : videoInactiveStyles
                                                                ]}
                                                                poster={image}
                                                                autoPlay={true}
                                                                ref={(
                                                                    element
                                                                ) =>
                                                                    (carouselRefs.current[
                                                                        index
                                                                    ] = element)
                                                                }
                                                            >
                                                                {image}
                                                                <source
                                                                    src={
                                                                        videoLink
                                                                    }
                                                                    type="video/mp4"
                                                                />
                                                            </video>
                                                        </HeroTooltip>
                                                    )
                                                )}
                                            </div>

                                            <div css={carouselDesktopStyles}>
                                                <button
                                                    css={[
                                                        buttonResetStyle,
                                                        buttonLeftStyles
                                                    ]}
                                                    aria-label={
                                                        leftArrowTranslatedLabel
                                                    }
                                                    onClick={() => {
                                                        handleChangeSlide(
                                                            "prev"
                                                        );
                                                    }}
                                                    {...leftArrowDataEventProps}
                                                >
                                                    <Picture
                                                        image={{
                                                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/circle-chevron-left.svg",
                                                            alt: leftArrowTranslatedLabel
                                                        }}
                                                    />
                                                </button>

                                                <button
                                                    css={[
                                                        buttonResetStyle,
                                                        buttonRightStyles
                                                    ]}
                                                    aria-label={
                                                        rightArrowTranslatedLabel
                                                    }
                                                    className="button-right"
                                                    onClick={() => {
                                                        handleChangeSlide(
                                                            "next"
                                                        );
                                                    }}
                                                    {...rightArrowDataEventProps}
                                                >
                                                    <Picture
                                                        image={{
                                                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/circle-chevron-right.svg",
                                                            alt: rightArrowTranslatedLabel
                                                        }}
                                                    />
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    <div css={backgroundStyles} />
                                </div>
                            </div>
                        </Row>
                    </div>
                </Row>
            </section>
        </UFOSegment>
    );
};

const tooltipStyle = css({
    padding: "14.452px",
    borderRadius: "38.375px",
    background: "var(--Primary-Yellow-400, #FCA700)"
});

const jiraHeroStyles = css({
    textAlign: "center",
    overflow: "hidden",
    marginBlockEnd: "40px"
});

const heroTitleStyles = css({
    marginBlock: "19px"
});

const heroGetStartedStyles = css({
    marginBlock: "30px 40px",

    [media.below.sm]: {
        marginBlock: "20px 45px"
    }
});

const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});

const tabsStyle = css({
    display: "flex",
    justifyContent: "space-between",
    margin: "0 auto",
    marginBlockEnd: "50px"
});

const teamCardStyles = css({
    "boxSizing": "border-box",
    "display": "flex",
    "flexDirection": "column",
    "width": "173px",
    "height": "90px",
    "alignItems": "center",
    "padding": "8px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".hover-icon": {
        display: "none"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".default-icon": {
            display: "none"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hover-icon": {
            display: "block"
        }
    }
});

const iconContainerStyles = css({
    height: "48px",
    width: "48px"
});

const teamCardTextStyles = css({
    fontFamily: "var(--bxp-charlie-display-font-family)",
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: "#101214"
});

const teamCardActiveStyles = css({
    border: "1px solid #101214",
    borderRadius: "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: "700"
    }
});

const teamCardsVisible = css({
    [media.below.md]: {
        "@keyframes pull": {
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "0%": {
                marginInlineStart: "0",
                transform: "margin"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "50%": {
                marginInlineStart: "-120px",
                transform: "margin"
            },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            "100%": {
                marginInlineStart: "0",
                transform: "margin"
            }
        },
        "animation": "2s pull forwards"
    }
});

const videoContainerStyles = css({
    "@keyframes fadein": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0.7
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1
        }
    },

    "width": "100%",
    "aspectRatio": "16/9",
    "position": "relative",
    "marginBlockEnd": "8%"
});

const videoImgContainerStyles = css({
    width: "100%",
    aspectRatio: "16/9",
    borderRadius: "5px",
    border: "3px solid var(--Neutral-Light-200, #F0F1F2)",
    background: token("color.text.inverse")
});

const videoInactiveStyles = css({
    display: "none"
});

const videoActiveStyles = css({
    display: "block"
});

const carouselStyles = css({
    margin: "0 auto",

    [media.below.md]: {
        width: "90%"
    }
});

const carouselDesktopStyles = css({
    [media.below.md]: {
        display: "none"
    }
});

const buttonLeftStyles = css({
    zIndex: "2",
    position: "absolute",
    width: "fit-content",
    insetBlockStart: "40%",
    insetInlineStart: "-100px",

    [media.below.md]: {
        insetInlineStart: "-75px"
    },

    [media.below.sm]: {
        display: "none"
    }
});

const buttonRightStyles = css({
    zIndex: "2",
    position: "absolute",
    width: "fit-content",
    insetBlockStart: "40%",
    insetInlineEnd: "-100px",

    [media.below.md]: {
        insetInlineEnd: "-75px"
    },

    [media.below.sm]: {
        display: "none"
    }
});

const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "58%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: "#1868DB",
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 0, 50% 0, 50% 10%, 98.5% 0%, 100% 85%, 17.75% 100%, 17.25% 85%, 0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});
